import {
    featureNeedsLandmarks,
    getInferenceDimensions,
    getRequiredModelIds,
    parseScaleSteps,
} from '../domain/analysis';
import { ApplicationError } from '../domain/errors';

import type { AnalysisConfig, LandmarkModelId } from '../domain/analysis';
import type { FaceDetectionOptions } from 'face-api.js';

type FaceApi = typeof import('face-api.js');
type MediaElement = HTMLImageElement | HTMLVideoElement | HTMLCanvasElement;

interface RawFaceResult {
    detection: { score: number; box: { x: number; y: number; width: number; height: number } };
    landmarks?: { positions: Array<{ x: number; y: number }> };
    descriptor?: Float32Array;
    expressions?: {
        neutral: number;
        happy: number;
        sad: number;
        angry: number;
        fearful: number;
        disgusted: number;
        surprised: number;
    };
    age?: number;
    gender?: string;
    genderProbability?: number;
}

interface PreparedMedia {
    input: MediaElement;
    scaleX: number;
    scaleY: number;
}

export interface FaceResult {
    id: string;
    score: number;
    box: { x: number; y: number; width: number; height: number };
    landmarks?: Array<{ x: number; y: number }>;
    descriptor?: Float32Array;
    expressions?: Record<string, number>;
    age?: number;
    gender?: string;
    genderProbability?: number;
    match?: { referenceIndex?: number; distance: number; isMatch: boolean };
}

export interface AnalysisResult {
    reference: FaceResult[];
    haystack: FaceResult[];
    durationMs: number;
    loadedModels: string[];
}

export type AnalysisProgress =
    { stage: 'models'; count: number } | { stage: 'reference' } | { stage: 'target' };

const faceApiPromise: Promise<FaceApi> = import('face-api.js');
const modelLoads = new Map<string, Promise<void>>();

const loaderNames: Record<string, keyof FaceApi['nets']> = {
    tinyFaceDetector: 'tinyFaceDetector',
    ssdMobilenetv1: 'ssdMobilenetv1',
    mtcnn: 'mtcnn',
    tinyYolov2: 'tinyYolov2',
    faceLandmark68Net: 'faceLandmark68Net',
    faceLandmark68TinyNet: 'faceLandmark68TinyNet',
    faceRecognitionNet: 'faceRecognitionNet',
    faceExpressionNet: 'faceExpressionNet',
    ageGenderNet: 'ageGenderNet',
};

async function ensureModel(faceapi: FaceApi, id: string, modelBaseUrl: string): Promise<void> {
    const existing = modelLoads.get(`${modelBaseUrl}:${id}`);
    if (existing !== undefined) return existing;

    const netName = loaderNames[id];
    if (netName === undefined) throw new ApplicationError('unknownModel', { model: id });

    const net = faceapi.nets[netName];
    const load = net.loadFromUri(modelBaseUrl).catch((error: unknown) => {
        modelLoads.delete(`${modelBaseUrl}:${id}`);
        throw error;
    });
    modelLoads.set(`${modelBaseUrl}:${id}`, load);
    return load;
}

async function ensureModels(
    faceapi: FaceApi,
    config: AnalysisConfig,
    onProgress: (progress: AnalysisProgress) => void
): Promise<string[]> {
    const ids = getRequiredModelIds(config);
    onProgress({ stage: 'models', count: ids.length });
    try {
        await Promise.all(ids.map((id) => ensureModel(faceapi, id, config.modelBaseUrl)));
    } catch (error) {
        if (error instanceof ApplicationError) throw error;
        throw new ApplicationError('modelLoadFailed');
    }
    return ids;
}

function createDetectorOptions(faceapi: FaceApi, config: AnalysisConfig): FaceDetectionOptions {
    const { detectorSettings } = config;
    switch (config.detector) {
        case 'ssdMobilenetv1':
            return new faceapi.SsdMobilenetv1Options(detectorSettings.ssd);
        case 'tinyFaceDetector':
            return new faceapi.TinyFaceDetectorOptions(detectorSettings.tiny);
        case 'tinyYolov2':
            return new faceapi.TinyYolov2Options(detectorSettings.tiny);
        case 'mtcnn': {
            const settings = detectorSettings.mtcnn;
            return new faceapi.MtcnnOptions({
                minFaceSize: settings.minFaceSize,
                scaleFactor: settings.scaleFactor,
                maxNumScales: settings.maxNumScales,
                scoreThresholds: settings.scoreThresholds,
                scaleSteps: settings.useCustomScaleSteps
                    ? parseScaleSteps(settings.scaleSteps)
                    : undefined,
            });
        }
    }
}

async function runPipeline(
    faceapi: FaceApi,
    media: MediaElement,
    config: AnalysisConfig,
    needsDescriptors: boolean
): Promise<RawFaceResult[]> {
    const options = createDetectorOptions(faceapi, config);
    const baseTask = faceapi.detectAllFaces(media, options);
    const needsLandmarks = featureNeedsLandmarks(config) || needsDescriptors;
    const { expressions, ageAndGender } = config.features;

    if (needsLandmarks) {
        const landmarksTask = baseTask.withFaceLandmarks(
            config.landmarkModel === ('faceLandmark68TinyNet' satisfies LandmarkModelId)
        );

        if (needsDescriptors) {
            const descriptorsTask = landmarksTask.withFaceDescriptors();
            if (expressions && ageAndGender) {
                return descriptorsTask.withFaceExpressions().withAgeAndGender();
            }
            if (expressions) {
                return descriptorsTask.withFaceExpressions();
            }
            if (ageAndGender) {
                return descriptorsTask.withAgeAndGender();
            }
            return descriptorsTask;
        }

        if (expressions && ageAndGender) {
            return landmarksTask.withFaceExpressions().withAgeAndGender();
        }
        if (expressions) {
            return landmarksTask.withFaceExpressions();
        }
        if (ageAndGender) {
            return landmarksTask.withAgeAndGender();
        }
        return landmarksTask;
    }

    if (expressions && ageAndGender) {
        return baseTask.withFaceExpressions().withAgeAndGender();
    }
    if (expressions) {
        return baseTask.withFaceExpressions();
    }
    if (ageAndGender) {
        return baseTask.withAgeAndGender();
    }

    const detections = await baseTask;
    return detections.map((detection) => ({ detection }));
}

function prepareMedia(media: MediaElement, maxInputDimension: number): PreparedMedia {
    const sourceWidth =
        media instanceof HTMLVideoElement
            ? media.videoWidth
            : media instanceof HTMLImageElement
              ? media.naturalWidth
              : media.width;
    const sourceHeight =
        media instanceof HTMLVideoElement
            ? media.videoHeight
            : media instanceof HTMLImageElement
              ? media.naturalHeight
              : media.height;
    const dimensions = getInferenceDimensions(sourceWidth, sourceHeight, maxInputDimension);
    if (dimensions.scaleX === 1 && dimensions.scaleY === 1) {
        return { input: media, scaleX: 1, scaleY: 1 };
    }
    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext('2d');
    if (context === null) throw new ApplicationError('canvasUnavailable');
    context.drawImage(media, 0, 0, dimensions.width, dimensions.height);
    return { input: canvas, scaleX: dimensions.scaleX, scaleY: dimensions.scaleY };
}

function normalizeFace(
    result: RawFaceResult,
    index: number,
    role: string,
    scaleX: number,
    scaleY: number
): FaceResult {
    const expressions =
        result.expressions !== undefined
            ? {
                  neutral: result.expressions.neutral,
                  happy: result.expressions.happy,
                  sad: result.expressions.sad,
                  angry: result.expressions.angry,
                  fearful: result.expressions.fearful,
                  disgusted: result.expressions.disgusted,
                  surprised: result.expressions.surprised,
              }
            : undefined;

    return {
        id: `${role}-${String(index)}`,
        score: result.detection.score,
        box: {
            x: result.detection.box.x * scaleX,
            y: result.detection.box.y * scaleY,
            width: result.detection.box.width * scaleX,
            height: result.detection.box.height * scaleY,
        },
        landmarks: result.landmarks?.positions.map(({ x, y }) => ({
            x: x * scaleX,
            y: y * scaleY,
        })),
        descriptor: result.descriptor,
        expressions,
        age: result.age,
        gender: result.gender,
        genderProbability: result.genderProbability,
    };
}

function attachMatches(
    faceapi: FaceApi,
    reference: FaceResult[],
    haystack: FaceResult[],
    threshold: number
): FaceResult[] {
    const labeled = reference.flatMap((face, index) =>
        face.descriptor !== undefined
            ? [new faceapi.LabeledFaceDescriptors(`reference-${String(index)}`, [face.descriptor])]
            : []
    );
    if (labeled.length === 0) return haystack;

    const matcher = new faceapi.FaceMatcher(labeled, threshold);
    return haystack.map((face) => {
        if (face.descriptor === undefined) return face;
        const best = matcher.findBestMatch(face.descriptor);
        const matchIndex = best.label.startsWith('reference-')
            ? Number(best.label.slice('reference-'.length))
            : undefined;
        return {
            ...face,
            match: {
                referenceIndex: Number.isInteger(matchIndex) ? matchIndex : undefined,
                distance: best.distance,
                isMatch: best.label !== 'unknown',
            },
        };
    });
}

export async function analyzeMedia({
    referenceMedia,
    haystackMedia,
    config,
    onProgress,
}: {
    referenceMedia?: MediaElement;
    haystackMedia: MediaElement;
    config: AnalysisConfig;
    onProgress: (progress: AnalysisProgress) => void;
}): Promise<AnalysisResult> {
    const start = performance.now();
    const faceapi = await faceApiPromise;
    const loadedModels = await ensureModels(faceapi, config, onProgress);

    let reference: FaceResult[] = [];
    if (config.features.matching) {
        if (referenceMedia === undefined) throw new ApplicationError('referenceRequired');
        onProgress({ stage: 'reference' });
        const preparedReference = prepareMedia(referenceMedia, config.maxInputDimension);
        const rawReference = await runPipeline(faceapi, preparedReference.input, config, true);
        reference = rawReference.map((face, index) =>
            normalizeFace(
                face,
                index,
                'reference',
                preparedReference.scaleX,
                preparedReference.scaleY
            )
        );
        if (reference.length === 0) throw new ApplicationError('referenceNotFound');
    }

    onProgress({ stage: 'target' });
    const preparedHaystack = prepareMedia(haystackMedia, config.maxInputDimension);
    const rawHaystack = await runPipeline(
        faceapi,
        preparedHaystack.input,
        config,
        config.features.matching
    );
    let haystack = rawHaystack.map((face, index) =>
        normalizeFace(face, index, 'haystack', preparedHaystack.scaleX, preparedHaystack.scaleY)
    );

    if (config.features.matching) {
        haystack = attachMatches(faceapi, reference, haystack, config.distanceThreshold);
    }

    return { reference, haystack, durationMs: performance.now() - start, loadedModels };
}
